import { Decorative, TopShop } from "@faire/design-tokens";
import {
  Action,
  Core,
  Expressive,
  Message,
  Program,
} from "@faire/slate/foundation/color";
import { ColorTokenKey } from "@faire/web-api--source/indigofair/data/ColorTokenKey";
import { getEventTheme } from "@faire/web/slate/Theme/eventsThemeColors";

export function getColor(
  colorTokenKey: keyof typeof ColorTokenKey | undefined,
  defaultValue: string
): string;
export function getColor(
  colorTokenKey: keyof typeof ColorTokenKey | undefined,
  defaultValue?: string
): string | undefined;
export function getColor(
  colorTokenKey: keyof typeof ColorTokenKey | undefined,
  defaultValue?: string
): string | undefined {
  if (
    !colorTokenKey ||
    colorTokenKey === ColorTokenKey.COLOR_TOKEN_KEY_UNKNOWN
  ) {
    return defaultValue;
  }
  return colorTokenMap[colorTokenKey];
}

const colorTokenMap: Partial<Record<ColorTokenKey, string>> = {
  [ColorTokenKey.ACTION_BORDER_DEFAULT]: Action.border.default,
  [ColorTokenKey.ACTION_BORDER_DISABLED]: Action.border.disabled,
  [ColorTokenKey.ACTION_BORDER_ENABLED]: Action.border.active,
  [ColorTokenKey.ACTION_BORDER_HOVER]: Action.border.hover,
  [ColorTokenKey.ACTION_BORDER_MUTED]: Action.border.disabled,
  [ColorTokenKey.ACTION_BORDER_SUBDUED]: Core.border.subdued,
  [ColorTokenKey.ACTION_ICON_DEFAULT]: Action.icon.default,
  [ColorTokenKey.ACTION_ICON_DISABLED]: Action.icon.disabled,
  [ColorTokenKey.ACTION_ICON_ENABLED]: Action.icon.active,
  [ColorTokenKey.ACTION_ICON_HOVER]: Action.icon.hover,
  [ColorTokenKey.ACTION_ICON_MUTED]: Action.icon.inverse,
  [ColorTokenKey.ACTION_ICON_SUBDUED]: Action.icon.disabled,
  [ColorTokenKey.ACTION_SURFACE_DEFAULT]: Action.surface.default,
  [ColorTokenKey.ACTION_SURFACE_DISABLED]: Action.surface.disabled,
  [ColorTokenKey.ACTION_SURFACE_ENABLED]: Action.surface.active,
  [ColorTokenKey.ACTION_SURFACE_HOVER]: Action.surface.hover,
  [ColorTokenKey.ACTION_SURFACE_MUTED]: Action.surface.inverse,
  [ColorTokenKey.ACTION_SURFACE_SUBDUED]: Action.surface.disabled,
  [ColorTokenKey.ACTION_TEXT_DEFAULT]: Action.text.default,
  [ColorTokenKey.ACTION_TEXT_DISABLED]: Action.text.disabled,
  [ColorTokenKey.ACTION_TEXT_ENABLED]: Action.text.active,
  [ColorTokenKey.ACTION_TEXT_HOVER]: Action.text.hover,
  [ColorTokenKey.ACTION_TEXT_MUTED]: Action.text.inverse,
  [ColorTokenKey.ACTION_TEXT_SUBDUED]: Action.text.disabled,
  [ColorTokenKey.CORE_BACKGROUND_PRIMARY]: Core.surface.primary,
  [ColorTokenKey.CORE_BACKGROUND_SECONDARY]: Core.surface.secondary,
  [ColorTokenKey.CORE_BACKGROUND_TERTIARY]: Core.surface.tertiary,
  [ColorTokenKey.CORE_BORDER_CRITICAL]: Message.border.critical,
  [ColorTokenKey.CORE_BORDER_PRIMARY]: Core.border.muted,
  [ColorTokenKey.CORE_BORDER_SUCCESS]: Message.border.success,
  [ColorTokenKey.CORE_BORDER_WARNING]: Message.border.warning,
  [ColorTokenKey.CORE_ICON_CRITICAL]: Message.icon.critical,
  [ColorTokenKey.CORE_ICON_PRIMARY]: Core.icon.primary,
  [ColorTokenKey.CORE_ICON_SECONDARY]: Core.icon.subdued,
  [ColorTokenKey.CORE_ICON_SUCCESS]: Message.icon.success,
  [ColorTokenKey.CORE_ICON_WARNING]: Message.icon.warning,
  [ColorTokenKey.CORE_SURFACE_CRITICAL]: Message.surface.critical,
  [ColorTokenKey.CORE_SURFACE_SUCCESS]: Message.surface.success,
  [ColorTokenKey.CORE_SURFACE_WARNING]: Message.surface.warning,
  [ColorTokenKey.CORE_TEXT_CRITICAL]: Message.text.critical,
  [ColorTokenKey.CORE_TEXT_PRIMARY]: Core.text.primary,
  [ColorTokenKey.CORE_TEXT_PRIMARY_INVERSE]: Core.text.primaryInverse,
  [ColorTokenKey.CORE_TEXT_SECONDARY]: Core.text.subdued,
  [ColorTokenKey.DECORATIVE_ICON_10]: Decorative.icon.neutral, // TODO: Missing equivalent
  [ColorTokenKey.DECORATIVE_ICON_20]: Decorative.icon.red, // TODO: Missing equivalent
  [ColorTokenKey.DECORATIVE_ICON_30]: Expressive.text.orange,
  [ColorTokenKey.DECORATIVE_ICON_40]: Decorative.icon.yellow,
  [ColorTokenKey.DECORATIVE_ICON_50]: Program.surface.top_shop.secondary,
  [ColorTokenKey.DECORATIVE_ICON_60]: Program.icon.insider,
  [ColorTokenKey.DECORATIVE_ICON_70]: Message.border.info,
  [ColorTokenKey.DECORATIVE_SHADE_10]: Expressive.text.neutral,
  [ColorTokenKey.DECORATIVE_SHADE_20]: Expressive.text.red,
  [ColorTokenKey.DECORATIVE_SHADE_30]: Message.text.sale,
  [ColorTokenKey.DECORATIVE_SHADE_40]: Expressive.icon.yellow,
  [ColorTokenKey.DECORATIVE_SHADE_50]: Expressive.icon.green,
  [ColorTokenKey.DECORATIVE_SHADE_60]: Program.text.insider,
  [ColorTokenKey.DECORATIVE_SHADE_70]: Expressive.text.blue,
  [ColorTokenKey.DECORATIVE_TINT_10]: Expressive.surface.neutral,
  [ColorTokenKey.DECORATIVE_TINT_20]: Expressive.surface.red,
  [ColorTokenKey.DECORATIVE_TINT_30]: Expressive.surface.orange,
  [ColorTokenKey.DECORATIVE_TINT_40]: Expressive.surface.yellow,
  [ColorTokenKey.DECORATIVE_TINT_50]: Expressive.surface.green,
  [ColorTokenKey.DECORATIVE_TINT_60]: Program.surface.insider.primary,
  [ColorTokenKey.DECORATIVE_TINT_70]: Expressive.surface.blue,
  [ColorTokenKey.TOP_SHOP_ICON_GREEN]: TopShop.icon.green, // TODO: Missing equivalent
  [ColorTokenKey.TOP_SHOP_SHADE_GREEN]: TopShop.shade.green, // TODO: Missing equivalent
  [ColorTokenKey.TOP_SHOP_TINT_GREEN]: TopShop.tint.green, // TODO: Missing equivalent
  [ColorTokenKey.CORE_SURFACE_DISABLED]: Core.surface.tertiary,
  [ColorTokenKey.CORE_SURFACE_ENABLED]: Action.surface.active,
  [ColorTokenKey.CORE_SURFACE_PRIMARY_MARKET]:
    getEventTheme("faireMarket").primaryDark,
  [ColorTokenKey.CORE_BADGE_ICON_MARKET]:
    getEventTheme("faireMarket").badgeIcon,
  [ColorTokenKey.CORE_BACKGROUND_PRIMARY_MARKET]:
    getEventTheme("faireMarket").lightBackground,
};
